'use client';

import React, { useEffect, useState, type ReactNode } from 'react';
import { Country } from '@/constants/country/country';
import { Currency, CurrencyCountry } from '@/constants/currency/currency';
import { CART_QUERY_KEYS } from '@/infrastructure/cart/utils/keys';
import { PRODUCT_QUERY_KEYS } from '@/infrastructure/product/utils/keys';
import Cookies from 'js-cookie';
import { COUNTRY_KEY, CURRENCY_KEY } from '@/constants/browser/cookie';
import { isClient } from '@/utils/function/env';
import { createSafeContext } from '@/utils/react/createSafeContext';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { env } from '@/utils/env';

const DEFAULT_COUNTRY: Country = Country.Singapore;
const DEFAULT_CURRENCY: Currency = Currency.Singapore;

interface RegionCurrencyContextValue {
  country: Country;
  currency: Currency;
  setCountry: (country: Country) => void;
  setCurrency: (currency: Currency) => void;
}

const [RegionCurrencyProvider, useRegionCurrencyContext] =
  createSafeContext<RegionCurrencyContextValue>('RegionCurrencyContext');

interface RegionCurrencyProviderProps {
  children: ReactNode;
  initialCountry?: Country;
  initialCurrency?: Currency;
}

/**
 * Function to get the cookie domain
 */
const getCookieDomain = () => {
  return env('NEXT_PUBLIC_ENV') === 'LOCAL' ? 'localhost' : undefined;
};

const getCountryCookie = () => {
  return Cookies.get(COUNTRY_KEY) as Country | undefined;
};

const getCurrencyCookie = () => {
  return Cookies.get(CURRENCY_KEY) as Currency | undefined;
};

const setCountryCookie = (country: Country): void => {
  if (isClient()) {
    const cookieOptions = {
      domain: getCookieDomain(),
      expires: 30,
    };

    Cookies.set(COUNTRY_KEY, country, cookieOptions);
  }
};

const setCurrencyCookie = (currency: Currency): void => {
  if (isClient()) {
    const cookieOptions = {
      domain: getCookieDomain(),
      expires: 30,
    };

    Cookies.set(CURRENCY_KEY, currency, cookieOptions);
  }
};

const invalidateRelatedQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.default() });
  queryClient.invalidateQueries({ queryKey: CART_QUERY_KEYS.default() });
};

export function RegionCurrencyContextProvider({
  children,
  initialCountry: serverCountryProp,
  initialCurrency: serverCurrencyProp,
}: RegionCurrencyProviderProps) {
  const getCurrency = () => {
    return serverCurrencyProp || getCurrencyCookie() || DEFAULT_CURRENCY;
  };

  const getCountry = () => {
    if (serverCurrencyProp) {
      return CurrencyCountry[serverCurrencyProp];
    }
    return getCountryCookie() || DEFAULT_COUNTRY;
  };

  // NOTE: We are using currency to set shipping country. so country and currency cannot be different.
  // But It can be different in future, so I seperated the functions.
  const initialCountry = getCountry();
  const initialCurrency = getCurrency();

  const [country, setCountryState] = useState<Country>(initialCountry);
  const [currency, setCurrencyState] = useState<Currency>(initialCurrency);

  const queryClient = useQueryClient();
  const setCountry = (newCountry: Country) => {
    if (newCountry === country) {
      return;
    }
    setCountryState(newCountry);
    setCountryCookie(newCountry);
    invalidateRelatedQueries(queryClient);
  };

  const setCurrency = (newCurrency: Currency) => {
    if (newCurrency === currency) {
      return;
    }
    setCurrencyState(newCurrency);
    setCurrencyCookie(newCurrency);
    invalidateRelatedQueries(queryClient);
  };

  useEffect(() => {
    return () => {
      invalidateRelatedQueries(queryClient);
    };
  }, [queryClient]);

  useEffect(() => {
    if (!initialCountry) {
      setCountryCookie(DEFAULT_COUNTRY);
    }
    if (!initialCurrency) {
      setCurrencyCookie(DEFAULT_CURRENCY);
    }
  }, [initialCountry, initialCurrency]);

  return (
    <RegionCurrencyProvider
      value={{
        country,
        currency,
        setCountry,
        setCurrency,
      }}
    >
      {children}
    </RegionCurrencyProvider>
  );
}

export const useRegionCurrency = useRegionCurrencyContext;
